import { apiGet, responseErrorMessage } from './api_v2';
import db from './db';

export const WIDGET_BOARD_RESOURCE = 'widget_board';
export const WIDGET_BOARD_SCHEMA_VERSION = 1;

const inFlightRequests = new Map();

function normalizedWorkspaceId(workspaceId) {
  return workspaceId == null ? null : String(workspaceId);
}

function coverageKey(workspaceId) {
  return [String(workspaceId), WIDGET_BOARD_RESOURCE];
}

function normalizeScopedRows(rows, workspaceId) {
  return (rows || []).filter((row) => row?.id != null).map((row) => ({ ...row, id: String(row.id), workspace_id: workspaceId }));
}

function isOptimisticBoardItem(row) {
  return String(row?.id || '').startsWith('optimistic:');
}

function preserveNonSnapshotBoardItem(row) {
  return row?.boardable_type !== 'widget' || isOptimisticBoardItem(row);
}

function coverageIsReady(coverage) {
  return coverage?.status === 'ready' && coverage.schema_version === WIDGET_BOARD_SCHEMA_VERSION;
}

async function pruneWorkspaceRows(table, workspaceId, keepIds, snapshotStartIds, preserve = () => false) {
  const rows = await table.where('workspace_id').equals(workspaceId).toArray();
  const keys = rows
    .filter((row) => snapshotStartIds.has(String(row.id)) && !preserve(row) && !keepIds.has(String(row.id)))
    .map((row) => [workspaceId, String(row.id)]);
  if (keys.length > 0) await table.bulkDelete(keys);
}

async function readSnapshotStart(workspaceId) {
  return db.transaction('r', db.widgets, db.board_items, db._resource_coverage, async () => {
    const coverage = await db._resource_coverage.get(coverageKey(workspaceId));
    if (coverage?.status === 'revoked') return null;

    const [widgets, boardItems] = await Promise.all([
      db.widgets.where('workspace_id').equals(workspaceId).toArray(),
      db.board_items.where('workspace_id').equals(workspaceId).toArray(),
    ]);
    return {
      coverage,
      accessEpoch: Number(coverage?.access_epoch || 0),
      widgetIds: new Set(widgets.map((row) => String(row.id))),
      boardItemIds: new Set(boardItems.map((row) => String(row.id))),
    };
  });
}

async function commitSnapshot(workspaceId, snapshot, snapshotStart) {
  const widgets = normalizeScopedRows(snapshot.widgets, workspaceId);
  const boardItems = normalizeScopedRows(snapshot.board_items, workspaceId);
  const widgetIds = new Set(widgets.map((row) => row.id));
  const boardItemIds = new Set(boardItems.map((row) => row.id));

  return db.transaction('rw', db.widgets, db.board_items, db._resource_coverage, async () => {
    const coverage = await db._resource_coverage.get(coverageKey(workspaceId));
    if (coverage?.status === 'revoked' || Number(coverage?.access_epoch || 0) !== snapshotStart.accessEpoch) return false;

    if (widgets.length > 0) await db.widgets.bulkPut(widgets);
    if (boardItems.length > 0) await db.board_items.bulkPut(boardItems);
    const optimisticWidgetIds = new Set(
      (await db.board_items.where('[workspace_id+boardable_type]').equals([workspaceId, 'widget']).toArray())
        .filter(isOptimisticBoardItem)
        .map((row) => String(row.boardable_id)),
    );
    await pruneWorkspaceRows(db.widgets, workspaceId, widgetIds, snapshotStart.widgetIds, (row) => optimisticWidgetIds.has(String(row.id)));
    await pruneWorkspaceRows(db.board_items, workspaceId, boardItemIds, snapshotStart.boardItemIds, preserveNonSnapshotBoardItem);

    // Readiness is written last in the same transaction as the authoritative rows.
    await db._resource_coverage.put({
      workspace_id: workspaceId,
      resource: WIDGET_BOARD_RESOURCE,
      status: 'ready',
      schema_version: WIDGET_BOARD_SCHEMA_VERSION,
      access_epoch: snapshotStart.accessEpoch,
    });
    return true;
  });
}

async function fetchAndCommitWorkspaceWidgetBoard(workspaceId, fetchSnapshot, snapshotStart) {
  const response = await fetchSnapshot(`/workspaces/${workspaceId}/widget_board_snapshot`);
  const data = await response.json();
  if (!(response.ok && data.ok && data.data?.widget_board_snapshot)) {
    throw new Error(responseErrorMessage(data, 'Could not load the widget board.'));
  }

  const snapshot = data.data.widget_board_snapshot;
  if (String(snapshot.workspace_id) !== workspaceId || Number(snapshot.schema_version) !== WIDGET_BOARD_SCHEMA_VERSION) {
    throw new Error('Invalid widget board snapshot');
  }

  return commitSnapshot(workspaceId, snapshot, snapshotStart);
}

export async function ensureWorkspaceWidgetBoard(workspaceId, options = {}) {
  const key = normalizedWorkspaceId(workspaceId);
  if (!key) return false;

  const snapshotStart = await readSnapshotStart(key);
  if (!snapshotStart) return false;
  if (!options.force && coverageIsReady(snapshotStart.coverage)) return true;

  const existing = inFlightRequests.get(key);
  if (existing?.accessEpoch === snapshotStart.accessEpoch) return existing.promise;
  if (existing) {
    existing.abortController.abort();
    inFlightRequests.delete(key);
  }

  const abortController = new AbortController();
  const fetchSnapshot = options.fetchSnapshot || ((path) => apiGet(path, { signal: abortController.signal }));
  const promise = fetchAndCommitWorkspaceWidgetBoard(key, fetchSnapshot, snapshotStart).finally(() => {
    if (inFlightRequests.get(key)?.promise === promise) inFlightRequests.delete(key);
  });
  inFlightRequests.set(key, { promise, abortController, accessEpoch: snapshotStart.accessEpoch });
  return promise;
}

export async function authorizeWorkspaceWidgetBoardsFromMembershipSnapshot(workspaces, { userAuthoritative = false } = {}) {
  if (!userAuthoritative) return [];

  const standardWorkspaceIds = (workspaces || [])
    .filter((workspace) => workspace?.id && workspace.kind === 'standard')
    .map((workspace) => String(workspace.id));

  await db.transaction('rw', db._resource_coverage, async () => {
    for (const workspaceId of standardWorkspaceIds) {
      const existing = await db._resource_coverage.get(coverageKey(workspaceId));
      if (existing?.status !== 'revoked') continue;
      await db._resource_coverage.put({
        workspace_id: workspaceId,
        resource: WIDGET_BOARD_RESOURCE,
        status: 'unknown',
        schema_version: WIDGET_BOARD_SCHEMA_VERSION,
        access_epoch: Number(existing.access_epoch || 0) + 1,
      });
    }
  });

  return standardWorkspaceIds;
}

export async function revokeWorkspaceWidgetBoard(workspaceId) {
  const key = normalizedWorkspaceId(workspaceId);
  if (!key) return;

  inFlightRequests.get(key)?.abortController.abort();
  inFlightRequests.delete(key);

  await db.transaction('rw', db.widgets, db.board_items, db._resource_coverage, async () => {
    const existing = await db._resource_coverage.get(coverageKey(key));
    await db.widgets.where('workspace_id').equals(key).delete();
    await db.board_items.where('workspace_id').equals(key).delete();
    await db._resource_coverage.put({
      workspace_id: key,
      resource: WIDGET_BOARD_RESOURCE,
      status: 'revoked',
      schema_version: WIDGET_BOARD_SCHEMA_VERSION,
      access_epoch: Number(existing?.access_epoch || 0) + (existing?.status === 'revoked' ? 0 : 1),
    });
  });
}

export function resetWorkspaceWidgetBoardStoreForTests() {
  for (const request of inFlightRequests.values()) request.abortController.abort();
  inFlightRequests.clear();
}
